/**
 * SDK deprecation warning headers — OpenAPI documentation.
 *
 * The API may attach `zapier-sdk-deprecation-*` response headers to a
 * response — any response — when sdkapi's version gate or a trusted Zapier
 * upstream service needs SDK clients to raise a deprecation notice. The
 * version gate is a global response hook on the server, so every documented
 * response — SDK API and Builder Tools API alike — may carry the headers.
 *
 * `documentSdkDeprecationHeaders` is the single writer of that
 * documentation: the SDK spec generator applies it to the published
 * openapi.yaml, and the spec merge in @zapier/openapi-spec applies it to
 * openapi-merged.yaml so Builder Tools responses are covered too. The
 * mutation is idempotent — already-documented headers are preserved.
 *
 * The headers are documented inline (`required: false`) rather than as
 * `$ref`'d components: openapi-typescript v6 cannot see requiredness
 * through a header `$ref` and would type the headers as always-present,
 * overstating the conditional runtime contract.
 */

const SDK_DEPRECATION_RESPONSE_HEADERS = {
  "zapier-sdk-deprecation-id": {
    description:
      "Stable identifier for an active SDK deprecation notice; clients use it to dedupe repeated notices. Only present when a trusted Zapier service raises an SDK-visible deprecation notice.",
    required: false,
    schema: { type: "string" },
  },
  "zapier-sdk-deprecation-message": {
    description:
      "Human-readable SDK deprecation warning, displayed verbatim by the Zapier SDK. Only present when a trusted Zapier service raises an SDK-visible deprecation notice.",
    required: false,
    schema: { type: "string" },
  },
};

const HTTP_METHODS = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
  "head",
  "options",
] as const;

/**
 * Minimal structural view of an OpenAPI document; both the zod-to-openapi
 * generator output and the YAML-parsed merge documents satisfy it.
 */
interface OpenAPIDocumentLike {
  paths?: Record<string, unknown>;
}

/**
 * Document the SDK deprecation headers on every response.
 */
export function documentSdkDeprecationHeaders(
  document: OpenAPIDocumentLike,
): void {
  for (const pathItem of Object.values(document.paths ?? {})) {
    if (!pathItem || typeof pathItem !== "object") continue;
    for (const method of HTTP_METHODS) {
      const operation = (pathItem as Record<string, unknown>)[method];
      if (!operation || typeof operation !== "object") continue;
      const responses = (operation as Record<string, unknown>).responses;
      if (!responses || typeof responses !== "object") continue;
      for (const response of Object.values(responses)) {
        if (!response || typeof response !== "object") continue;
        const responseObject = response as {
          headers?: Record<string, unknown>;
        };
        responseObject.headers = {
          ...SDK_DEPRECATION_RESPONSE_HEADERS,
          ...responseObject.headers,
        };
      }
    }
  }
}
